import { createSlice } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';

// Initial state
export const initialState = {
  _schemaVersion: 3,
  fleetVehicleDataGrid: {
    all: {},
    created: {},
    order: {},
    customs: {},
    payment: {},
    dealer: {},
    procurement: {},
    fleet: {},
    cancelled: {},
    defleeted: {},
  },
  availabilityGrid: {},
  reservationsGrid: {},
  subscriptionsGrid: {},
  arnmRepairsGrid: {},
  arnmMaintenanceGrid: {},
  sellVehiclesGrid: {},
  contractsGrid: {},
  arnmAccidentsGrid: {},
  logisticsGrid: {},
  favoriteDashboards: [],
  creditCheckGrid: {},
  guaranteeReturnsGrid: {},
  tradeVehiclesGrid: {},
  tradeTicketsGrid: {},
  myIssuesGrid: {},
  genericTicketsGrid: {},
  forSaleVehiclesGrid: {},
  sellRequests: {
    sellRequestsGrid: {},
    sellRequestsViewMode: 'kanban',
  },
  pinnedFilters: {
    servicePoints: [],
    serviceVehicles: ['requestType', 'brand', 'status', 'assignee'],
    procurement: ['stage', 'supplier'],
    reservations: ['type', 'agent', 'subscriptionType'],
    fleetVehicles: [],
    availability: [
      'brand',
      'model',
      'transmission',
      'color',
      'procurementStageId',
      'engineType',
      'fuelType',
      'skuType',
      'bodyType',
      'status',
      'location',
      'sourcingType',
      'categoryClass',
      'reservationType',
      'condition',
    ],
    creditCheck: ['origin', 'stage', 'contractType'],
    subscriptions: [
      'status',
      'type',
      'bundleType',
      'bundleStatus',
      'deliveryStatus',
      'deliveryLocation',
      'labels',
      'assignee',
      'procurementStage',
      'reservationType',
      'availabilityStatus',
      'bookingAgent',
      'outOfSla',
      'customerTypes',
    ],
    arnmAccidents: ['reason', 'stage', 'servicePoint', 'assignee'],
    arnmRepairs: ['reason', 'stage', 'servicePoint', 'assignee'],
    arnmMaintenance: ['reason', 'stage', 'servicePoint', 'assignee'],
    subscriptionContracts: ['status', 'createdAt'],
    tradeTickets: ['stage', 'contractPeriod', 'assignee', 'createdAt'],
    trafficOffenses: ['assignee'],
    greenCard: ['assignee'],
    bookings: [
      'origin',
      'contractType',
      'contractPeriod',
      'customerTypes',
      'stage',
      'assignee',
      'labels',
      'wonTime',
    ],
    buyouts: ['status', 'assignee'],
    genericTickets: [
      'status',
      'team',
      'assignee',
      'dueDate',
      'priority',
      'reason',
      'createdBy',
      'createdAt',
    ],
    agents: ['role'],
    permissions: ['role'],
    sellRequests: [],
    catalogCars: ['brand', 'category', 'status'],
    catalogEbikes: ['brand', 'category', 'status'],
    defleets: [
      'brand',
      'model',
      'year',
      'status',
      'target',
      'subscriptionStatus',
      'promisedReturnDate',
      'returnDate',
      'monthlyFee',
      'assignee',
    ],
    logistics: [
      'servicePoint',
      'category',
      'status',
      'pickupLocation',
      'dropoffLocation',
      'assignee',
      'owner',
    ],
    privateVehicles: [],
    billing: ['assignee'],
    orders: ['status'],
    competition: ['brand', 'model'],
    tires: [],
    sellVehicles: ['brand', 'model', 'category', 'status'],
  },
  emails: {
    isComposeEmailOpen: false,
  },
};
// Actual Slice
export const uiSettingsSlice = createSlice({
  name: 'uiSettings',
  initialState,
  reducers: {
    saveFleetVehiclesDataGrid: (state, { payload }) => {
      if (!state.fleetVehicleDataGrid) {
        state.fleetVehicleDataGrid = {};
      }
      state.fleetVehicleDataGrid[payload.stage] = payload.gridState;
    },
    saveAvailabilityGrid: (state, { payload }) => {
      state.availabilityGrid = payload.gridState;
    },
    saveReservationsGrid: (state, { payload }) => {
      state.reservationsGrid = payload.gridState;
    },
    saveSubscriptionsGrid: (state, { payload }) => {
      state.subscriptionsGrid = payload.gridState;
    },
    saveArnmRepairsGrid: (state, { payload }) => {
      state.arnmRepairsGrid = payload.gridState;
    },
    saveArnmMaintenanceGrid: (state, { payload }) => {
      state.arnmMaintenanceGrid = payload.gridState;
    },
    saveSellVehiclesGrid: (state, { payload }) => {
      state.sellVehiclesGrid = payload.gridState;
    },
    saveArnmAccidentsGrid: (state, { payload }) => {
      state.arnmAccidentsGrid = payload.gridState;
    },
    saveLogisticsGrid: (state, { payload }) => {
      state.logisticsGrid = payload.gridState;
    },
    toggleFavoriteDashboard: (state, { payload }) => {
      const favoriteRoute = payload;

      if (!favoriteRoute?.key || !favoriteRoute?.url) return;

      if (!state.favoriteDashboards) {
        state.favoriteDashboards = [];
      }

      const exists = state.favoriteDashboards.some(
        (d) => d.key === favoriteRoute.key
      );

      if (exists) {
        state.favoriteDashboards = state.favoriteDashboards.filter(
          (d) => d.key !== favoriteRoute.key
        );
      } else {
        state.favoriteDashboards.push(favoriteRoute);
      }
    },
    saveCreditCheckGrid: (state, { payload }) => {
      state.creditCheckGrid = payload.gridState;
    },
    saveTradeTicketsGrid: (state, { payload }) => {
      state.tradeTicketsGrid = payload.gridState;
    },
    saveMyIssuesGrid: (state, { payload }) => {
      state.myIssuesGrid = payload.gridState;
    },
    saveGenericTicketsGrid: (state, { payload }) => {
      state.genericTicketsGrid = payload.gridState;
    },
    saveForSaleVehiclesGrid: (state, { payload }) => {
      state.forSaleVehiclesGrid = payload.gridState;
    },
    saveGuaranteeReturnsGrid: (state, { payload }) => {
      state.guaranteeReturnsGrid = payload.gridState;
    },
    saveSellRequestsGrid: (state, { payload }) => {
      if (!state.sellRequests) {
        state.sellRequests = {};
      }
      state.sellRequests.sellRequestsGrid = payload.gridState;
    },
    saveSellRequestsViewMode: (state, { payload }) => {
      if (!state.sellRequests) {
        state.sellRequests = {};
      }
      state.sellRequests.sellRequestsViewMode = payload.viewMode;
    },
    togglePinnedFilters: (state, { payload }) => {
      if (!state.pinnedFilters) {
        state.pinnedFilters = {};
      }
      if (!state.pinnedFilters[payload.board]) {
        state.pinnedFilters[payload.board] = [
          ...(initialState.pinnedFilters[payload.board] || []),
        ];
      }

      const exists = state.pinnedFilters[payload.board].find(
        (filter) => filter === payload.pinnedFilter
      );

      if (!exists) {
        state.pinnedFilters[payload.board].push(payload.pinnedFilter);
      } else {
        state.pinnedFilters[payload.board] = state.pinnedFilters[
          payload.board
        ].filter((filter) => filter !== payload.pinnedFilter);
      }
    },
    openComposeEmail: (state) => {
      if (!state.emails) {
        state.emails = { isComposeEmailOpen: true };
        return;
      }
      state.emails.isComposeEmailOpen = true;
    },
    closeComposeEmail: (state) => {
      if (!state.emails) {
        state.emails = { isComposeEmailOpen: false };
        return;
      }
      state.emails.isComposeEmailOpen = false;
    },
    // Special reducer for hydrating the state. Special case for next-redux-wrapper
    extraReducers: {
      [HYDRATE]: (state, action) => {
        return {
          ...state,
          ...action.payload.errors,
        };
      },
    },
  },
});

export const {
  saveFleetVehiclesDataGrid,
  saveAvailabilityGrid,
  saveReservationsGrid,
  saveSubscriptionsGrid,
  saveArnmRepairsGrid,
  saveArnmMaintenanceGrid,
  saveSellVehiclesGrid,
  saveArnmAccidentsGrid,
  saveLogisticsGrid,
  toggleFavoriteDashboard,
  saveCreditCheckGrid,
  saveGuaranteeReturnsGrid,
  saveTradeVehiclesGrid,
  saveTradeTicketsGrid,
  saveMyIssuesGrid,
  saveGenericTicketsGrid,
  saveForSaleVehiclesGrid,
  saveSellRequestsGrid,
  saveSellRequestsViewMode,
  togglePinnedFilters,
  openComposeEmail,
  closeComposeEmail,
} = uiSettingsSlice.actions;

export const saveFleetVehiclesDataGridState = ({ gridState, stage }) => {
  return async (dispatch) => {
    dispatch(saveFleetVehiclesDataGrid({ gridState, stage }));
  };
};

export const saveAvailabilityGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveAvailabilityGrid({ gridState }));
  };
};

export const saveReservationsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveReservationsGrid({ gridState }));
  };
};

export const saveSubscriptionsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveSubscriptionsGrid({ gridState }));
  };
};

export const saveArnmRepairsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveArnmRepairsGrid({ gridState }));
  };
};

export const saveArnmMaintenanceGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveArnmMaintenanceGrid({ gridState }));
  };
};

export const saveSellVehiclesGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveSellVehiclesGrid({ gridState }));
  };
};

export const saveArnmAccidentsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveArnmAccidentsGrid({ gridState }));
  };
};
export const saveLogisticsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveLogisticsGrid({ gridState }));
  };
};
export const toggleFavoriteDashboardState = ({ key, title, url }) => {
  return async (dispatch) => {
    dispatch(toggleFavoriteDashboard({ key, title, url }));
  };
};
export const saveCreditCheckGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveCreditCheckGrid({ gridState }));
  };
};
export const saveGuaranteeReturnsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveGuaranteeReturnsGrid({ gridState }));
  };
};

export const saveTradeTicketsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveTradeTicketsGrid({ gridState }));
  };
};

export const saveMyIssuesGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveMyIssuesGrid({ gridState }));
  };
};

export const saveGenericTicketsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveGenericTicketsGrid({ gridState }));
  };
};

export const saveForSaleVehiclesGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveForSaleVehiclesGrid({ gridState }));
  };
};

export const saveSellRequestsGridState = ({ gridState }) => {
  return async (dispatch) => {
    dispatch(saveSellRequestsGrid({ gridState }));
  };
};

export const saveSellRequestsViewModeState = ({ viewMode }) => {
  return async (dispatch) => {
    dispatch(saveSellRequestsViewMode({ viewMode }));
  };
};

export const togglePinnedFiltersState = ({ board, pinnedFilter }) => {
  return (dispatch) => {
    dispatch(togglePinnedFilters({ board, pinnedFilter }));
  };
};

export const openComposeEmailWindow = () => {
  return (dispatch) => {
    dispatch(openComposeEmail());
  };
};

export const closeComposeEmailWindow = () => {
  return (dispatch) => {
    dispatch(closeComposeEmail());
  };
};
// selectors
export const selectUiSettingsState = (state) => state.uiSettings;

// GRIDS STATE SELECTORS
export const selectFleetVehiclesDataGridState = (state) =>
  state.uiSettings.fleetVehicleDataGrid || {};
export const selectAvailabilityDataGridState = (state) =>
  state.uiSettings.availabilityGrid;
export const selectReservationsGridDataGridState = (state) =>
  state.uiSettings.reservationsGrid;
export const selectSubscriptionsDataGridState = (state) =>
  state.uiSettings.subscriptionsGrid;
export const selectArnmRepairsGridDataState = (state) =>
  state.uiSettings.arnmRepairsGrid;
export const selectArnmMaintenanceGridState = (state) =>
  state.uiSettings.arnmMaintenanceGrid;
export const selectSellVehiclesGridState = (state) =>
  state.uiSettings.sellVehiclesGrid;
export const selectArnmAccidentsGridDataState = (state) =>
  state.uiSettings.arnmAccidentsGrid;
export const selectLogisticsDataGridState = (state) =>
  state.uiSettings.logisticsGrid;
export const selectFavoriteDashboards = (state) =>
  state.uiSettings.favoriteDashboards;
export const selectCreditCheckDataGridState = (state) =>
  state.uiSettings.creditCheckGrid;
export const selectGuaranteeReturnsDataGridState = (state) =>
  state.uiSettings.guaranteeReturnsGrid;
export const selectTradeVehiclesGridState = (state) =>
  state.uiSettings.tradeVehiclesGrid;
export const selectTradeTicketsGridState = (state) =>
  state.uiSettings.tradeTicketsGrid;
export const selectMyIssuesGridState = (state) => state.uiSettings.myIssuesGrid;
export const selectGenericTicketsDataGridState = (state) =>
  state.uiSettings.genericTicketsGrid;
export const selectForSaleVehiclesDataGridState = (state) =>
  state.uiSettings.forSaleVehiclesGrid;
export const selectSellRequestsGridDataState = (state) =>
  state.uiSettings.sellRequests?.sellRequestsGrid;
export const selectSellRequestsViewMode = (state) =>
  state.uiSettings.sellRequests?.sellRequestsViewMode ||
  initialState.sellRequests.sellRequestsViewMode;

// PINNED FILTERS SELECTORS
export const selectPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters || initialState.pinnedFilters;
export const selectServicePointsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.servicePoints ??
  initialState.pinnedFilters.servicePoints;
export const selectSubscriptionsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.subscriptions ??
  initialState.pinnedFilters.subscriptions;
export const selectArnmAccidentsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.arnmAccidents ??
  initialState.pinnedFilters.arnmAccidents;
export const selectArnmRepairsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.arnmRepairs ??
  initialState.pinnedFilters.arnmRepairs;
export const selectArnmMaintenancePinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.arnmMaintenance ??
  initialState.pinnedFilters.arnmMaintenance;
export const selectServiceVehiclesPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.serviceVehicles ??
  initialState.pinnedFilters.serviceVehicles;
export const selectFleetVehiclesPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.fleetVehicles ??
  initialState.pinnedFilters.fleetVehicles;
export const selectTrafficOffensesPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.trafficOffenses ??
  initialState.pinnedFilters.trafficOffenses;
export const selectGreenCardPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.greenCard ??
  initialState.pinnedFilters.greenCard;
export const selectAvailabilityPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.availability ??
  initialState.pinnedFilters.availability;
export const selectBookingsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.bookings ??
  initialState.pinnedFilters.bookings;
export const selectAgentsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.agents ?? initialState.pinnedFilters.agents;
export const selectPermissionsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.permissions ??
  initialState.pinnedFilters.permissions;
export const selectCatalogCarsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.catalogCars ??
  initialState.pinnedFilters.catalogCars;
export const selectCatalogEbikesPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.catalogEbikes ??
  initialState.pinnedFilters.catalogEbikes;
export const selectBillingPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.billing ?? initialState.pinnedFilters.billing;
export const selectProcurementPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.procurement ??
  initialState.pinnedFilters.procurement;
export const selectReservationsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.reservations ??
  initialState.pinnedFilters.reservations;
export const selectCreditCheckPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.creditCheck ??
  initialState.pinnedFilters.creditCheck;
export const selectBuyoutsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.buyouts ?? initialState.pinnedFilters.buyouts;
export const selectContractsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.subscriptionContracts ??
  initialState.pinnedFilters.subscriptionContracts;
export const selectTiresPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.tires ?? initialState.pinnedFilters.tires;
export const selectSellRequestsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.sellRequests ??
  initialState.pinnedFilters.sellRequests;
export const selectDefleetsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.defleets ??
  initialState.pinnedFilters.defleets;
export const selectTradeTicketsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.tradeTickets ??
  initialState.pinnedFilters.tradeTickets;
export const selectPrivateVehiclesPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.privateVehicles ??
  initialState.pinnedFilters.privateVehicles;
export const selectOrdersPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.orders ?? initialState.pinnedFilters.orders;
export const selectGenericTicketsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.genericTickets ??
  initialState.pinnedFilters.genericTickets;
export const selectCompetitionPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.competition ??
  initialState.pinnedFilters.competition;
export const selectLogisticsPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.logistics ??
  initialState.pinnedFilters.logistics;
export const selectSellVehiclesPinnedFilters = (state) =>
  state.uiSettings.pinnedFilters?.sellVehicles ??
  initialState.pinnedFilters.sellVehicles;

export const selectEmailsView = (state) => state.uiSettings.emails || {};

export default uiSettingsSlice.reducer;
